<template>
  <modal 
    name="dismiss-agreement" 
    class="modal-inner" 
    transition="pop-out" 
    :width="modalWidth" 
    :focus-trap="true" 
    height="auto" 
    :scrollable="true" 
    :adaptive="true" 
    :reset="true"
    @before-open="beforeOpen"
  >
    <span class="close-button" @click="hide('dismiss-agreement')">
      <BaseIcon icon="times-circle" class="text-white"/>
    </span>
    <div class="modal-header text-lg bg-primary-50 font-serif">
      {{ $t("dismiss_agreement.title") }}
    </div>
    <div class="modal-body">
      <!-- Info Text -->
      <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <BaseIcon icon="exclamation-triangle" class="text-yellow-400" />
          </div>
          <div class="ml-3">
            <p class="text-sm text-yellow-700">
              {{ $t("cancel_agreement.info_text") }}
            </p>
          </div>
        </div>
      </div>

      <!-- Agreement Info Display -->
      <div class="mb-4 p-3 bg-gray-50 rounded">
        <div class="text-sm">
          <div><strong>{{ $t("cancel_agreement.agreement_number") }}:</strong> {{ currentAgreement?.agreement_number }}</div>
          <div><strong>{{ $t("cancel_agreement.customer_name") }}:</strong> {{ currentAgreement?.customer_name }}</div>
        </div>
      </div>

      <!-- PDF Upload - Uppsägningsblankett -->
      <div class="form-group mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          {{ $t("cancel_agreement.upload_cancellation_form") }} <span v-if="!isBypassUser">*</span>
        </label>
        <div class="relative">
          <input 
            type="file" 
            accept=".pdf"
            @change="handleFileUpload"
            ref="fileInput"
            class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          />
          <div class="w-full border rounded-md px-3 py-2 flex items-center" :class="errors.file ? 'border-red-500' : 'border-gray-300'">
            <span class="btn-blue-outline px-3 py-1 mr-3">{{ $t("file_input.choose_file") }}</span>
            <span class="text-gray-500 text-sm truncate">{{ selectedFile ? selectedFile.name : $t("file_input.no_file_selected") }}</span>
          </div>
        </div>
        <span v-if="selectedFile" class="text-xs text-gray-600 mt-1">
          {{ formatFileSize(selectedFile.size) }}
        </span>
        <span v-if="errors.file" class="text-red-500 text-xs mt-1 block">{{ errors.file }}</span>
      </div>

      <div class="form-group mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          {{ $t("cancel_agreement.cancellation_date") }} *
        </label>
        <input 
          type="date"
          v-model="dismissalDate"
          class="w-full border border-gray-300 rounded-md px-3 py-2"
          :class="{ 'border-red-500': errors.date }"
        />
        <span v-if="errors.date" class="text-red-500 text-xs mt-1 block">{{ errors.date }}</span>
      </div>

      <!-- Billing Method Dropdown -->
       
      <div class="form-group mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          {{ $t("cancel_agreement.billing_method_label") }} *
        </label>
        <select 
          v-model="billingMethod"
          class="w-full border border-gray-300 rounded-md px-3 py-2"
          :class="{ 'border-red-500': errors.billing }"
        >
          <option value="">{{ $t("cancel_agreement.select_billing_method") }}</option>
          <option value="as_before">{{ $t("cancel_agreement.billing_as_before") }}</option>
          <option value="final_invoice">{{ $t("cancel_agreement.final_invoice") }}</option>
          <option value="no_invoice">{{ $t("cancel_agreement.no_invoice") }}</option>
        </select>
        <span v-if="errors.billing" class="text-red-500 text-xs mt-1 block">{{ errors.billing }}</span>
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end gap-3 mt-6">
        <button 
          class="btn-secondary-outline" 
          @click="hide('dismiss-agreement')"
        >
          {{ $t("user_actions.cancel") }}
        </button>
        <button 
          class="btn-red" 
          @click="submitDismissAgreement"
          :disabled="isSubmitting"
        >
          <BaseIcon v-if="isSubmitting" icon="spinner" spin class="mr-2" />
          {{ $t("dismiss_agreement.title") }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
const MODAL_WIDTH = 600;

export default {
  props: {
    agreement: {
      type: Object,
      required: false,
      default: null
    }
  },

  data() {
    return {
      isSubmitting: false,
      selectedFile: null,
      dismissalDate: '',
      billingMethod: '',
      errors: {
        file: '',
        date: '',
        billing: ''
      },
      currentAgreement: null
    };
  },

  computed: {
    modalWidth() {
      return MODAL_WIDTH;
    },
    isBypassUser() {
      const user = this.$store.state.user;
      return user && user.username === 'amaper';
    },
  },

  methods: {
    beforeOpen(event) {
      // Get agreement from event params or prop
      this.currentAgreement = event.params?.agreement || this.agreement;
      
      // Reset form
      this.selectedFile = null;
      this.dismissalDate = '';
      this.billingMethod = '';
      this.errors = { file: '', date: '', billing: '' };
      
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      
      // Validate file type
      if (file && file.type !== 'application/pdf') {
        this.errors.file = this.$t('cancel_agreement.error_invalid_file_type');
        this.selectedFile = null;
        return;
      }
      
      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file && file.size > maxSize) {
        this.errors.file = this.$t('cancel_agreement.error_file_too_large');
        this.selectedFile = null;
        return;
      }
      
      this.selectedFile = file;
      this.errors.file = '';
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    validate() {
      this.errors = { file: '', date: '', billing: '' };
      let isValid = true;
      
      if (!this.selectedFile && !this.isBypassUser) {
        this.errors.file = this.$t('cancel_agreement.error_file_required');
        isValid = false;
      }
      
      if (!this.dismissalDate) {
        this.errors.date = this.$t('cancel_agreement.error_date_required');
        isValid = false;
      }
      
      if (!this.billingMethod) {
        this.errors.billing = this.$t('cancel_agreement.error_billing_required');
        isValid = false;
      }
      
      return isValid;
    },

    async submitDismissAgreement() {
      if (!this.validate()) {
        return;
      }

      this.isSubmitting = true;

      try {
        const formData = new FormData();
        if (this.selectedFile) {
          formData.append('pdf', this.selectedFile);
        }
        formData.append('dismissal_date', this.dismissalDate);
        formData.append('billing_method', this.billingMethod);
        if (this.isBypassUser) {
          formData.append('skip_email', 'true');
          formData.append('skip_pdf', 'true');
        }
        
        const response = await this.axios.put(
          `${process.env.VUE_APP_SERVER_URL}/agreement/dismiss/${this.currentAgreement._id}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        if (response.status === 200) {
          this.handleSuccess(this.$t('cancel_agreement.success_message'));
        } else {
          throw new Error(this.$t('cancel_agreement.error_submission_failed'));
        }

        this.hide('dismiss-agreement');
        this.$emit('agreement-dismissed');
        
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isSubmitting = false;
      }
    },

    hide(id) {
      this.$modal.hide(id);
    }
  }
};
</script>
